import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FieldMeta } from '../columns/columnModel';
import { effectiveSort, fieldAccess, withoutForbidden } from '../columns/fieldAccess';
import { withRequiredFields } from '../columns/columnModel';
import { columnsFrom, newColumnId, setColumnWidth } from './columnOps';
import { sanitizeDraft, sanitizeView } from './sanitizeView';
import type { FieldKey, TableView, TableViewPrefs, ViewColumn, ViewContent, ViewSort, ViewVisibility } from './types';
import { DEFAULT_PAGE_SIZE } from './types';
import {
  canRemoveView,
  cloneContent,
  createView,
  diffContent,
  duplicateView,
  hiddenViews,
  isOwnView,
  moveView,
  normalizeViewName,
  orderedViews,
  removeActionFor,
  resolveActiveViewId,
  saveViewContent,
  unhideView,
  viewerPagingMode,
  withViewerWidths,
  withFiltersFrom,
  withPagingOverride,
  withWidthOverride,
} from './viewOps';
import { TableViewStore, TableViewStoreError, ViewDraftStore } from './viewStore';
import { loadOrCreateViews } from './loadViews';

export interface UseTableViewsArgs {
  /** Off for a table without tabs: nothing is loaded or stored, and the state stays empty. */
  enabled: boolean;
  tableKey: string;
  userId: string;
  store: TableViewStore;
  drafts: ViewDraftStore;
  fields: FieldMeta[];
  defaultColumns: FieldKey[][];
  defaultSort: ViewSort;
  /** Name of the tab created for a user who has none yet ("Vše"). */
  firstViewName: string;
  /** Builds a copy's name from the original's ("Mine (kopie)"). */
  copyName: (name: string) => string;
  /** The table's live filter, sort and page size (useFilter) — the working copy's. */
  live: { filter: Record<string, unknown>; sort: ViewSort; pageSize: number };
  /**
   * The filters the user saved in the old list, imported as tabs on their first visit (no views yet).
   * Each gets the table's default sort and columns. Rejecting, or resolving empty, imports nothing.
   */
  importLegacy?: () => Promise<{ name: string; filter: Record<string, unknown> }[]>;
  /** Pushes a tab's filter, sort and page size into the table's live state. */
  applyLive: (content: Pick<ViewContent, 'filter' | 'sort'> & { pageSize: number }) => void;
}

/**
 * State of the tab strip of a RingilDataTable v3: the user's views, which one is open, and the
 * unsaved working copy of it.
 *
 * The working copy is split on purpose. Filter and sort are the table's live `useFilter` state —
 * that hook already restores them across reloads and from links — so they are read, not copied.
 * Columns and hearted filters have no other home, so they live here. Every edit that leaves the
 * working copy different from the saved tab is mirrored into the draft store, which is how an
 * unsaved change survives switching tabs and reloading.
 */
export const useTableViews = (args: UseTableViewsArgs) => {
  const { tableKey, userId, store, drafts, fields, defaultSort } = args;
  const isAllowed = useMemo(() => fieldAccess(fields), [fields]);
  const knownKeys = useMemo(() => fields.map(field => field.key), [fields]);
  const allowedSortFields = useMemo(() => fields.filter(field => field.sortField && isAllowed(field.key)).map(field => field.sortField), [fields, isAllowed]);

  const defaultColumns = useCallback(() => withRequiredFields(columnsFrom(args.defaultColumns), fields, newColumnId), [args.defaultColumns, fields]);
  const sanitizeCtx = useMemo(() => ({ tableKey, knownKeys, defaultColumns, defaultSort }), [tableKey, knownKeys, defaultColumns, defaultSort]);

  const [views, setViews] = useState<TableView[]>([]);
  const [prefs, setPrefs] = useState<TableViewPrefs>({ tableKey, order: [], hidden: [] });
  const [activeId, setActiveId] = useState<string>();
  const [columns, setColumns] = useState<ViewColumn[]>([]);
  const [favouriteFilters, setFavouriteFilters] = useState<string[] | undefined>();
  const [pagingMode, setPagingMode] = useState<ViewContent['pagingMode']>();
  const [ready, setReady] = useState(false);

  // The latest values for callbacks that run after awaits, so they never act on a stale render.
  const latest = useRef({ views, prefs, activeId, columns, favouriteFilters, live: args.live });
  latest.current = { views, prefs, activeId, columns, favouriteFilters, live: args.live };
  // The current render's setter: the load pushes a tab's filter after an await, when the effect's own
  // render is long gone and its useFilter callbacks would write over state restored since.
  const applyLive = useRef(args.applyLive);
  applyLive.current = args.applyLive;

  const visible = useMemo(() => orderedViews(views, prefs), [views, prefs]);
  const activeView = views.find(view => view.id === activeId);

  const working: ViewContent | undefined = activeView
    ? {
        filter: args.live.filter ?? {},
        sort: args.live.sort,
        columns,
        ...(favouriteFilters !== undefined ? { favouriteFilters } : {}),
        pageSize: args.live.pageSize,
        ...(pagingMode !== undefined ? { pagingMode } : {}),
      }
    : undefined;
  const diff = activeView && working ? diffContent(activeView, working) : undefined;
  const modified = !!diff && Object.values(diff).some(Boolean);

  /** Puts a tab's content (its draft when it has one) into the working copy and the live filter. */
  const open = (view: TableView, content: ViewContent, applyFilterAndSort: boolean) => {
    setActiveId(view.id);
    setColumns(withRequiredFields(content.columns, fields, newColumnId));
    setFavouriteFilters(content.favouriteFilters);
    setPagingMode(content.pagingMode);
    if (applyFilterAndSort)
      applyLive.current({
        filter: content.filter,
        sort: effectiveSort(content.sort, allowedSortFields, defaultSort),
        pageSize: content.pageSize ?? DEFAULT_PAGE_SIZE,
      });
  };

  const contentFor = (view: TableView): ViewContent => sanitizeDraft(drafts.getDrafts(tableKey)[view.id], sanitizeCtx) ?? view;

  // Every user starts with one ordinary tab — not a special "all" that can't be edited.
  const firstView = () =>
    createView({
      tableKey,
      name: args.firstViewName,
      ownerId: userId,
      content: { filter: {}, sort: defaultSort, columns: defaultColumns() },
      now: new Date().toISOString(),
    });

  // ---- load once per table and user
  useEffect(() => {
    if (!args.enabled) return;
    let cancelled = false;
    (async () => {
      const importLegacy = args.importLegacy
        ? async () =>
            (await args.importLegacy())
              .filter(saved => normalizeViewName(saved.name))
              .map(saved =>
                createView({
                  tableKey,
                  name: saved.name,
                  ownerId: userId,
                  content: { filter: saved.filter ?? {}, sort: defaultSort, columns: defaultColumns() },
                  now: new Date().toISOString(),
                }),
              )
        : undefined;
      const loadedRaw = await loadOrCreateViews(store, { userId, tableKey }, firstView, importLegacy).catch(error => {
        if (!(error instanceof TableViewStoreError)) throw error;
        // The store could not be read (and has said so): the list still works, on one tab that is not saved.
        const fallback = firstView();
        return { views: [fallback], prefs: { tableKey, order: [fallback.id], hidden: [], activeViewId: fallback.id } };
      });
      const loaded = loadedRaw.views.map(raw => sanitizeView(raw, sanitizeCtx)).filter(Boolean);
      const nextPrefs = loadedRaw.prefs;
      if (cancelled) return;
      setViews(loaded);
      setPrefs(nextPrefs);
      const shown = orderedViews(loaded, nextPrefs);
      const active = shown.find(view => view.id === resolveActiveViewId(shown, nextPrefs));
      // The tab decides what the table shows, its unsaved draft first. What useFilter restored (the URL,
      // the last session) can belong to another tab, or be older than the tab — a colleague may have
      // saved their shared tab since — so it is replaced, not taken as this tab's working copy.
      if (active) open(active, contentFor(active), true);
      setReady(true);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [args.enabled, tableKey, userId]);

  // ---- keep the active tab's draft in step with the working copy
  useEffect(() => {
    if (!ready || !activeView || !working) return;
    drafts.setDraft(tableKey, activeView.id, modified ? cloneContent(working) : undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, activeView?.id, modified, JSON.stringify(working)]);

  const persistPrefs = async (next: TableViewPrefs) => {
    setPrefs(next);
    await store.savePrefs(next);
  };

  const replaceView = (view: TableView) =>
    setViews(current => (current.some(v => v.id === view.id) ? current.map(v => (v.id === view.id ? view : v)) : [...current, view]));

  /**
   * After a conflict: the views as they are now on the server. The working copy is left alone, so an
   * unsaved edit stays an unsaved edit on top of the newer tab and can be saved again. A tab that is
   * gone meanwhile hands over to the first one.
   */
  const refresh = async () => {
    const fresh = (await store.load(tableKey)).views.map(raw => sanitizeView(raw, sanitizeCtx)).filter(Boolean);
    setViews(fresh);
    if (!fresh.some(view => view.id === latest.current.activeId)) {
      const first = orderedViews(fresh, latest.current.prefs)[0];
      if (first) open(first, contentFor(first), true);
    }
  };

  /**
   * A store failure has already been shown to the user; the action just ends with its state untouched.
   * A conflict also reloads the views, so the next save sends the current version.
   */
  const guard =
    <A extends unknown[]>(action: (...a: A) => Promise<void>) =>
    async (...a: A): Promise<void> => {
      try {
        await action(...a);
      } catch (error) {
        if (!(error instanceof TableViewStoreError)) throw error;
        if (error.kind === 'conflict') await refresh().catch(() => undefined);
      }
    };

  const select = async (viewId: string) => {
    const target = latest.current.views.find(view => view.id === viewId);
    if (!target || viewId === latest.current.activeId) return;
    open(target, contentFor(target), true);
    await persistPrefs({ ...latest.current.prefs, activeViewId: viewId });
  };

  /** Inserts a new tab right after `afterId` in the user's order and opens it. */
  const addAndOpen = async (draft: TableView, afterId?: string, applyFilterAndSort = true) => {
    // The store gives the view its real id and version.
    const view = await store.createView(draft);
    replaceView(view);
    const order = orderedViews(latest.current.views, latest.current.prefs).map(v => v.id);
    const at = afterId ? order.indexOf(afterId) + 1 : order.length;
    order.splice(at < 1 ? order.length : at, 0, view.id);
    open(view, view, applyFilterAndSort);
    await persistPrefs({ ...latest.current.prefs, order: [...order, ...latest.current.prefs.hidden], activeViewId: view.id });
  };

  /** "Nový pohled": an empty tab with the table's defaults, after the current one. */
  const create = async (name: string) => {
    const clean = normalizeViewName(name);
    if (!clean) return;
    const view = createView({
      tableKey,
      name: clean,
      ownerId: userId,
      content: { filter: {}, sort: defaultSort, columns: defaultColumns() },
      now: new Date().toISOString(),
    });
    await addAndOpen(view, latest.current.activeId);
  };

  /** "Uložit jako nový": the working copy becomes a new tab of the user's; the original goes back to its saved state. */
  const saveAsNew = async (name: string) => {
    const clean = normalizeViewName(name);
    const source = activeView;
    if (!clean || !source || !working) return;
    const content = withoutForbidden(working, isAllowed, allowedSortFields, defaultSort);
    const view = createView({ tableKey, name: clean, ownerId: userId, content, now: new Date().toISOString() });
    // The live filter already is the new tab's, so it is not pushed again.
    await addAndOpen(view, source.id, false);
    // Only once the new tab exists: had it failed, the edit would still be the original's unsaved one.
    drafts.setDraft(tableKey, source.id, undefined);
  };

  /** "Uložit": only the owner saves; the hidden (forbidden) fields stay, because the working copy holds them. */
  const save = async () => {
    if (!activeView || !working || !isOwnView(activeView, userId)) return;
    const saved = await store.updateView(saveViewContent(activeView, working, new Date().toISOString()));
    replaceView(saved);
    drafts.setDraft(tableKey, saved.id, undefined);
  };

  /** "Uložit filtr" (default-filters sidebar): saves filter, sort and hearts; column edits stay unsaved. */
  const saveFilters = async () => {
    if (!activeView || !working || !isOwnView(activeView, userId)) return;
    const saved = await store.updateView(saveViewContent(activeView, withFiltersFrom(activeView, working), new Date().toISOString()));
    replaceView(saved);
  };

  /** "Zahodit změny": back to the saved tab. */
  const revert = () => {
    if (!activeView) return;
    drafts.setDraft(tableKey, activeView.id, undefined);
    open(activeView, activeView, true);
  };

  const rename = async (viewId: string, name: string) => {
    const view = latest.current.views.find(v => v.id === viewId);
    const clean = normalizeViewName(name);
    if (!view || !clean || !isOwnView(view, userId)) return;
    replaceView(await store.updateView({ ...view, name: clean, updatedAt: new Date().toISOString() }));
  };

  /**
   * "Sdílet pohled": the owner shares the saved tab with the company, or takes it back. Colleagues get
   * the saved version; an unsaved edit stays the owner's own until it is saved.
   */
  const share = async (viewId: string, visibility: ViewVisibility) => {
    const view = latest.current.views.find(v => v.id === viewId);
    if (!view || !isOwnView(view, userId) || view.visibility === visibility) return;
    replaceView(await store.setVisibility(view, visibility));
  };

  /** "Duplikovat": a private copy of the saved tab, holding only what this user can see. */
  const duplicate = async (viewId: string) => {
    const source = latest.current.views.find(v => v.id === viewId);
    if (!source) return;
    const copy = duplicateView(
      { ...source, ...withoutForbidden(source, isAllowed, allowedSortFields, defaultSort) },
      { name: args.copyName(source.name), ownerId: userId, now: new Date().toISOString() },
    );
    await addAndOpen(copy, source.id);
  };

  /** Deletes the user's own tab or hides a colleague's; never the last one. The neighbour opens if it was active. */
  const remove = async (viewId: string) => {
    const shown = orderedViews(latest.current.views, latest.current.prefs);
    const view = shown.find(v => v.id === viewId);
    if (!view || !canRemoveView(shown)) return;
    const index = shown.indexOf(view);
    const neighbour = shown[index - 1] ?? shown[index + 1];
    let nextPrefs: TableViewPrefs;
    if (removeActionFor(view, userId) === 'delete') {
      await store.deleteView(tableKey, viewId);
      setViews(current => current.filter(v => v.id !== viewId));
      nextPrefs = { ...latest.current.prefs, order: latest.current.prefs.order.filter(id => id !== viewId) };
    } else {
      nextPrefs = { ...latest.current.prefs, hidden: [...latest.current.prefs.hidden, viewId] };
    }
    drafts.setDraft(tableKey, viewId, undefined);
    if (latest.current.activeId === viewId && neighbour) {
      open(neighbour, contentFor(neighbour), true);
      nextPrefs = { ...nextPrefs, activeViewId: neighbour.id };
    }
    await persistPrefs(nextPrefs);
  };

  /** "Skryté pohledy": a hidden colleague's tab shows again, and opens. */
  const unhide = async (viewId: string) => {
    const view = latest.current.views.find(v => v.id === viewId);
    if (!view || !latest.current.prefs.hidden.includes(viewId)) return;
    open(view, contentFor(view), true);
    await persistPrefs(unhideView(latest.current.prefs, viewId));
  };

  const move = async (viewId: string, direction: -1 | 1) => {
    await persistPrefs(moveView(orderedViews(latest.current.views, latest.current.prefs), latest.current.prefs, viewId, direction));
  };

  return {
    ready,
    views: visible,
    /** Colleagues' tabs the user hid, which "Hidden views" lists to show again. */
    hiddenViews: hiddenViews(views, prefs),
    activeView,
    working,
    modified,
    diff,
    columns,
    setColumns,
    /** The columns as the table draws them: on a colleague's tab, with the widths this user dragged them to. */
    displayColumns: activeView && !isOwnView(activeView, userId) ? withViewerWidths(columns, prefs, activeView.id) : columns,
    /**
     * A dragged column edge. On an own tab the width goes into the working copy (saved with the tab);
     * on a colleague's it is the user's own, kept in their prefs — the shared tab is not changed.
     */
    resizeColumn: guard(async (columnId: string, width: number | undefined) => {
      const view = latest.current.views.find(v => v.id === latest.current.activeId);
      if (!view || isOwnView(view, userId)) {
        setColumns(current => setColumnWidth(current, columnId, width));
        return;
      }
      await persistPrefs(
        withWidthOverride(
          latest.current.prefs,
          view.id,
          columnId,
          width,
          latest.current.views.map(v => v.id),
        ),
      );
    }),
    /** The table's own columns, as a fresh tab gets them — for "Reset to default". */
    defaultColumns,
    favouriteFilters,
    setFavouriteFilters,
    /**
     * Pages or infinite scroll. On an own tab it is part of the working copy (saved with the tab);
     * on a colleague's it is the user's own choice, kept in their prefs — it never changes the shared tab.
     */
    pagingMode: activeView && !isOwnView(activeView, userId) ? viewerPagingMode(activeView, prefs) : pagingMode,
    setPagingMode: guard(async (mode: 'pages' | 'scroll') => {
      const view = latest.current.views.find(v => v.id === latest.current.activeId);
      if (!view || isOwnView(view, userId)) {
        setPagingMode(mode);
        return;
      }
      await persistPrefs(
        withPagingOverride(
          latest.current.prefs,
          view.id,
          mode,
          latest.current.views.map(v => v.id),
        ),
      );
    }),
    isOwn: (view: TableView) => isOwnView(view, userId),
    actions: {
      select: guard(select),
      create: guard(create),
      saveAsNew: guard(saveAsNew),
      save: guard(save),
      saveFilters: guard(saveFilters),
      revert,
      rename: guard(rename),
      share: guard(share),
      duplicate: guard(duplicate),
      remove: guard(remove),
      move: guard(move),
      unhide: guard(unhide),
    },
  };
};

export type TableViewsState = ReturnType<typeof useTableViews>;
